import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router";
import { useUiContextStore } from "../../../stores/uiContextStore";
import {
  Search,
  Plus,
  Loader2,
  FileText,
  Upload,
  Download,
  Ban,
  AlertTriangle,
  Pencil,
  Save,
  X,
  File,
  Eye,
  Trash2,
  Tag,
  FolderOpen,
  FilePlus,
  User,
} from "lucide-react";
import { DataTable, type Column } from "../../components/DataTable";
import { TableToolbar } from "../../components/TableToolbar";
import { StatusBadge } from "../../components/StatusBadge";
import { showToast } from "../../components/ui/custom-toast";
import {
  getPageSizeOptions,
  resolvePageSizeForViewMode,
} from "../../../utils/formatters";
import { Dropdown } from "../../components/Dropdown";
import {
  EntityCardGrid,
  MetaDates,
  TitleTooltip,
  VersionChip,
} from "../../components/EntityCardGrid";
import { SkeletonCard } from "../../components/Skeleton";
import { DeleteConfirmDialog } from "../../components/DeleteConfirmDialog";
import {
  Modal,
  FormField,
  FormInput,
  FormTextarea,
  FormSelect,
  ModalFooterButtons,
} from "../../components/Modal";
import { GovernanceUploadModal } from "../GovernanceUploadModal";
import { Tip } from "../../components/ui/tooltip";
import { API_URL } from "../../../constants";
import { useAuthStore } from "../../../stores/authStore";
import { RBACAction } from "../../components/RBACButton";
import { getAPI } from "../../../api/frameworksApi";
import {
  searchGovernanceDocuments,
  searchSupportingEvidencePaged,
  getGovernanceDocument,
  createGovernanceDocument,
  updateGovernanceDocument,
  deleteGovernanceDocument,
  deleteGovernanceDocumentFile,
  uploadGovernanceDocumentFile,
  downloadGovernanceDocumentFile,
  previewGovernanceDocumentFile,
  submitGovernanceDocumentForReview,
  reviewGovernanceDocument,
  createNewGovernanceDocumentVersion,
  retireGovernanceDocument,
  getGovernanceDocumentHistory,
  downloadGovernanceDocumentVersionFile,
  listSupportingEvidence,
  getSupportingEvidence,
  createSupportingEvidence,
  updateSupportingEvidence,
  deleteSupportingEvidence,
  uploadSupportingEvidenceFile,
  downloadSupportingEvidenceFile,
  previewSupportingEvidenceFile,
  searchEvidenceTags,
  type GovernanceDocumentResponse,
  type GovernanceDocumentType,
  type DocumentLifecycleStatus,
  type SupportingEvidenceResponse,
  type HistoryEvent,
} from "../../../api/frameworksApi";
import { useProcessingJobStore } from "../../../stores/processingJobStore";
import {
  DOCUMENT_TYPES,
  DOC_TYPE_COLORS,
  LIFECYCLE_STATUSES,
  TITLE_MAX_LENGTH,
  EVIDENCE_TITLE_MAX,
  DAYS_MAX,
  MAX_FILE_BYTES,
} from "./constants";
import type { GovDocRow, EvidenceRow } from "./types";
import {
  triggerDownload,
  mapLifecycleToVariant,
  formatDocType,
  getTitleValidationError,
  toGovDocRow,
  toEvidenceRow,
  formatFileSize,
  lifecycleActionLabel,
} from "./utils";
import { getGovDocColumns, getEvidenceColumns } from "./columns";
import { GovernanceDocumentDetail } from "./GovernanceDocumentDetail";
import { EvidenceDetail } from "./EvidenceDetail";
import { PreviewModal } from "./PreviewModal";

/**
 * UI column key -> backend `sortByColumn` field name.
 * Empty entries mean the UI key is passed through unchanged (see the
 * `?? docSortKey` / `?? evSortKey` fallbacks at the call sites). Populate
 * these only when the backend field name differs from the UI key.
 */
const GOV_SORT_COLUMNS: Record<string, string> = {};
const EV_SORT_COLUMNS: Record<string, string> = {};

/**
 * What each tab is called, rendered on the tab itself and published to the AI Companion.
 *
 * One constant for both so they cannot disagree: while these were literals in the buttons and the
 * companion carried its own copy, the panel announced "Documents" above a tab reading "Governance
 * Documents".
 */
const TAB_TITLE = {
  governance: "Governance Documents",
  evidence: "Supporting Evidence",
} as const;

export function ContentRegistry() {
  const navigate = useNavigate();
  const { id: docIdFromUrl } = useParams<{ id: string }>();
  const currentUser = useAuthStore((state) => state.user);

  /* ── Tab state ── */
  const [activeTab, setActiveTab] = useState<"governance" | "evidence">(
    "governance",
  );

  /* ── Governance Documents state ── */
  const [documents, setDocuments] = useState<GovDocRow[]>([]);
  const [govLoading, setGovLoading] = useState(true);
  const [govError, setGovError] = useState<string | null>(null);
  const [govSearch, setGovSearch] = useState("");
  // The keyword actually sent to the backend (committed on Enter / icon click).
  const [govKeyword, setGovKeyword] = useState("");
  const [govPage, setGovPage] = useState(1); // 1-based in this page's UI
  const [govTotal, setGovTotal] = useState(0);
  // True only while a *subsequent* page/sort/search fetch is in flight, so the
  // previous rows stay on screen instead of flashing back to a skeleton.
  const [govRefreshing, setGovRefreshing] = useState(false);
  // Governance Docs and Evidence maintain independent view-mode/page-size
  // state so switching one section's view has no effect on the other.
  const [govViewMode, setGovViewMode] = useState<"card" | "table">("card");
  // Must be a valid option for the default view mode above ("card") — the
  // plain table default (10) isn't one of [12,24,48,96].
  const [govPageSize, setGovPageSize] = useState(getPageSizeOptions("card")[0]);
  const [filterType, setFilterType] = useState<GovernanceDocumentType | "">("");
  const [filterStatus, setFilterStatus] = useState<
    DocumentLifecycleStatus | ""
  >("");
  const [selectedGovDoc, setSelectedGovDoc] =
    useState<GovernanceDocumentResponse | null>(null);
  const [govDetailLoading, setGovDetailLoading] = useState(false);
  const [docHistory, setDocHistory] = useState<HistoryEvent[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  /* ── Evidence state ── */
  const [evidence, setEvidence] = useState<EvidenceRow[]>([]);
  const [evidenceLoading, setEvidenceLoading] = useState(true);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  const [evidenceSearch, setEvidenceSearch] = useState("");
  // The keyword actually sent to the backend (committed on Enter / icon click).
  const [evKeyword, setEvKeyword] = useState("");
  const [evPage, setEvPage] = useState(1); // 1-based in this page's UI
  const [evTotal, setEvTotal] = useState(0);
  const [evRefreshing, setEvRefreshing] = useState(false);
  const [evViewMode, setEvViewMode] = useState<"card" | "table">("card");
  // Same reasoning as govPageSize above — must start valid for the default
  // "card" view mode.
  const [evPageSize, setEvPageSize] = useState(getPageSizeOptions("card")[0]);
  const [selectedEvidence, setSelectedEvidence] =
    useState<SupportingEvidenceResponse | null>(null);
  const [evidenceDetailLoading, setEvidenceDetailLoading] = useState(false);

  // Publish which half of this page is showing, the record open in it, and what the tab the user
  // clicked is called. The Documents / Evidence split is a tab in component state and the URL never
  // changes, so this is the only thing that can tell the two apart — and it is also what lets
  // SUPPORTING_EVIDENCE be recognised as the page the user is *on*, since that screen has no route of
  // its own. All of it goes in one call so the companion cannot observe a half-state.
  //
  // The title comes from `TAB_TITLE`, the same constant the tab buttons render, so the assistant cannot
  // announce a page name the tab does not use. Cleared on unmount.
  useEffect(() => {
    const onEvidence = activeTab === "evidence";
    const record = onEvidence ? selectedEvidence : selectedGovDoc;
    useUiContextStore
      .getState()
      .setPageContext(
        onEvidence ? "EVIDENCE" : "DOCUMENTS",
        record?.title ? { id: String(record.id), name: record.title } : null,
        onEvidence ? TAB_TITLE.evidence : TAB_TITLE.governance,
      );
    return () => useUiContextStore.getState().clearPageContext();
  }, [activeTab, selectedGovDoc, selectedEvidence]);

  const handleGovViewModeChange = (mode: "card" | "table") => {
    setGovViewMode(mode);
    setGovPageSize((prev) => resolvePageSizeForViewMode(prev, mode));
    setGovPage(1);
  };

  const handleEvViewModeChange = (mode: "card" | "table") => {
    setEvViewMode(mode);
    setEvPageSize((prev) => resolvePageSizeForViewMode(prev, mode));
    setEvPage(1);
  };

  /* ── Create Governance Doc modal state ── */
  const [showCreateGov, setShowCreateGov] = useState(false);
  const [createGovStep, setCreateGovStep] = useState<"details" | "upload">(
    "details",
  );
  const [createdGovDocId, setCreatedGovDocId] = useState<string | null>(null);
  const [newGovTitle, setNewGovTitle] = useState("");
  const [newGovDescription, setNewGovDescription] = useState("");
  const [newGovType, setNewGovType] = useState<GovernanceDocumentType | "">("");
  const [newGovReviewCycle, setNewGovReviewCycle] = useState<
    "" | "none" | "90" | "180" | "365" | "custom"
  >("");
  const [newGovCustomDays, setNewGovCustomDays] = useState("");
  const [createGovFile, setCreateGovFile] = useState<File | null>(null);
  /* Step 2 substate: PICKING → CREATING → UPLOADING (or ERROR). Once the run
     starts, `processingJobStore` owns it and the console takes over. */
  const [createGovStep2, setCreateGovStep2] = useState<
    "PICKING" | "CREATING" | "UPLOADING" | "ERROR"
  >("PICKING");
  const [savingCreateGov, setSavingCreateGov] = useState(false);
  const [createGovValidationError, setCreateGovValidationError] = useState<{
    title: string;
    message: string;
    code?: string;
  } | null>(null);
  /**
   * The validation run lives in `processingJobStore` so it survives this page
   * unmounting - the user can minimise the console to the floating bubble and
   * keep working. We only kick it off and react to how it ends.
   */
  const startJob = useProcessingJobStore((s) => s.start);
  const setOrphanDoc = useProcessingJobStore((s) => s.setOrphanDoc);
  const flushOrphanDoc = useProcessingJobStore((s) => s.flushOrphanDoc);
  const clearJob = useProcessingJobStore((s) => s.clear);
  const jobCompletionTick = useProcessingJobStore((s) => s.completionTick);
  const jobReopenPickerTick = useProcessingJobStore((s) => s.reopenPickerTick);
  /** A run is live (visible or minimised); the wizard stays out of its way. */
  const hasBackgroundJob = useProcessingJobStore((s) => s.job !== null);
  const createGovFileInputRef = React.useRef<HTMLInputElement>(null);

  // Accessible orgs for create
  const [accessibleOrgs, setAccessibleOrgs] = useState<
    { id: string; name: string; shortName: string }[]
  >([]);
  const [accessibleOrgsLoading, setAccessibleOrgsLoading] = useState(false);
  const [newGovOrgId, setNewGovOrgId] = useState("");

  /* ── Create Evidence modal state ── */
  const [showCreateEvidence, setShowCreateEvidence] = useState(false);
  const [createEvidenceStep, setCreateEvidenceStep] = useState<
    "details" | "upload"
  >("details");
  const [createdEvidenceId, setCreatedEvidenceId] = useState<string | null>(
    null,
  );
  const [creatingEvidence, setCreatingEvidence] = useState(false);
  const [newEvidenceTitle, setNewEvidenceTitle] = useState("");
  const [newEvidenceDescription, setNewEvidenceDescription] = useState("");
  const [newEvidenceTags, setNewEvidenceTags] = useState("");
  const [createEvidenceFile, setCreateEvidenceFile] = useState<File | null>(
    null,
  );
  const [uploadingCreateEvidenceFile, setUploadingCreateEvidenceFile] =
    useState(false);
  const createEvidenceFileInputRef = React.useRef<HTMLInputElement>(null);
  const [newEvidenceOrgId, setNewEvidenceOrgId] = useState("");
  const [newEvidenceReviewCycle, setNewEvidenceReviewCycle] = useState<
    "" | "none" | "90" | "180" | "365" | "custom"
  >("");
  const [newEvidenceCustomDays, setNewEvidenceCustomDays] = useState("");
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);

  /* ── Edit Governance Doc state ── */
  const pendingEditRef = React.useRef(false);
  const [editMode, setEditMode] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editReviewCycle, setEditReviewCycle] = useState<
    "none" | "90" | "180" | "365" | "custom"
  >("none");
  const [editCustomDays, setEditCustomDays] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  /* ── Edit Evidence state ── */
  const [editEvidenceMode, setEditEvidenceMode] = useState(false);
  const [editEvidenceTitle, setEditEvidenceTitle] = useState("");
  const [editEvidenceDescription, setEditEvidenceDescription] = useState("");
  const [editEvidenceTags, setEditEvidenceTags] = useState("");
  const [savingEvidenceEdit, setSavingEvidenceEdit] = useState(false);

  /* ── Edit Evidence Modal (from table) ── */
  const [showEditEvidenceModal, setShowEditEvidenceModal] = useState(false);
  const [editingEvidenceId, setEditingEvidenceId] = useState<string | null>(
    null,
  );

  /* ── Preview Evidence Modal (from table) ── */
  const [previewEvidenceRow, setPreviewEvidenceRow] =
    useState<EvidenceRow | null>(null);

  /* ── Gov docs sort state ── */
  const [docSortKey, setDocSortKey] = useState<string | null>(null);
  const [docSortDir, setDocSortDir] = useState<"asc" | "desc">("asc");
  // 3-state cycle: asc → desc → unsorted (back to the default original order).
  const handleDocSort = (key: string) => {
    setGovPage(1);
    if (docSortKey !== key) {
      setDocSortKey(key);
      setDocSortDir("asc");
    } else if (docSortDir === "asc") {
      setDocSortDir("desc");
    } else {
      setDocSortKey(null);
      setDocSortDir("asc");
    }
  };

  /* ── Evidence sort state ── */
  const [evSortKey, setEvSortKey] = useState<string | null>(null);
  const [evSortDir, setEvSortDir] = useState<"asc" | "desc">("asc");
  // 3-state cycle: asc → desc → unsorted (back to the default original order).
  const handleEvSort = (key: string) => {
    setEvPage(1);
    if (evSortKey !== key) {
      setEvSortKey(key);
      setEvSortDir("asc");
    } else if (evSortDir === "asc") {
      setEvSortDir("desc");
    } else {
      setEvSortKey(null);
      setEvSortDir("asc");
    }
  };

  /* ── Table preview state ── */
  const [tablePreviewDocId, setTablePreviewDocId] = useState<string | null>(
    null,
  );
  // Cache-bust token captured each time a preview opens. Replacing a file keeps
  // the same doc id (and often the same version), so without a fresh token the
  // browser would serve the stale cached PDF. A per-open timestamp guarantees
  // the current file is fetched.
  const [tablePreviewToken, setTablePreviewToken] = useState(0);
  const openTablePreview = useCallback((id: string) => {
    setTablePreviewDocId(id);
    setTablePreviewToken(Date.now());
  }, []);
  const closeTablePreview = useCallback(() => setTablePreviewDocId(null), []);

  /* ── Delete state ── */
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteTargetType, setDeleteTargetType] = useState<
    "governance" | "evidence"
  >("governance");

  /* ── Lifecycle modal state ── */
  const [showLifecycleModal, setShowLifecycleModal] = useState(false);
  const [lifecycleAction, setLifecycleAction] = useState<string>("");
  const [lifecycleReason, setLifecycleReason] = useState("");
  const [lifecycleProcessing, setLifecycleProcessing] = useState(false);

  /* ── Upload modal state ── */
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadTargetType, setUploadTargetType] = useState<
    "governance" | "evidence"
  >("governance");

  /* ── Fetch governance documents (server-side search/sort/filter/paginate) ── */
  // The skeleton is only shown before the very first page ever lands; every
  // later fetch keeps the current rows visible (no blank page on paging).
  const govFirstLoadDoneRef = useRef(false);
  const fetchGovDocuments = useCallback(async () => {
    setGovError(null);
    if (govFirstLoadDoneRef.current) setGovRefreshing(true);
    else setGovLoading(true);
    try {
      // The UI page is 1-based; the endpoint expects a 0-based page index.
      const res = await searchGovernanceDocuments(govPage - 1, govPageSize, {
        keyword: govKeyword.trim(),
        // Unsorted (the initial state) sends both sort fields as empty.
        sortByColumn: docSortKey
          ? (GOV_SORT_COLUMNS[docSortKey] ?? docSortKey)
          : "",
        sortingOrder: docSortKey ? docSortDir : "",
        type: filterType || null,
        status: filterStatus || null,
      });
      setDocuments(res.items.map(toGovDocRow));
      setGovTotal(res.totalItems);
      // The current page can fall off the end of the result set (a delete, a
      // larger page size, a narrower filter) — step back to the last real page.
      if (res.items.length === 0 && govPage > 1 && res.totalItems > 0) {
        setGovPage(Math.max(1, Math.ceil(res.totalItems / govPageSize)));
      }
    } catch (err: unknown) {
      setGovError(
        err instanceof Error ? err.message : "Failed to load documents",
      );
      setDocuments([]);
      setGovTotal(0);
    } finally {
      govFirstLoadDoneRef.current = true;
      setGovLoading(false);
      setGovRefreshing(false);
    }
  }, [
    govPage,
    govPageSize,
    govKeyword,
    docSortKey,
    docSortDir,
    filterType,
    filterStatus,
  ]);

  /* ── Fetch evidence (server-side search / sort / paginate) ── */
  const evFirstLoadDoneRef = useRef(false);
  const fetchEvidence = useCallback(async () => {
    setEvidenceError(null);
    if (evFirstLoadDoneRef.current) setEvRefreshing(true);
    else setEvidenceLoading(true);
    try {
      const res = await searchSupportingEvidencePaged(evPage - 1, evPageSize, {
        keyword: evKeyword.trim(),
        sortByColumn: evSortKey ? (EV_SORT_COLUMNS[evSortKey] ?? evSortKey) : "",
        sortingOrder: evSortKey ? evSortDir : "",
      });
      setEvidence(res.items.map(toEvidenceRow));
      setEvTotal(res.totalItems);
      if (res.items.length === 0 && evPage > 1 && res.totalItems > 0) {
        setEvPage(Math.max(1, Math.ceil(res.totalItems / evPageSize)));
      }
    } catch (err: unknown) {
      setEvidenceError(
        err instanceof Error ? err.message : "Failed to load evidence",
      );
      setEvidence([]);
      setEvTotal(0);
    } finally {
      evFirstLoadDoneRef.current = true;
      setEvidenceLoading(false);
      setEvRefreshing(false);
    }
  }, [evPage, evPageSize, evKeyword, evSortKey, evSortDir]);

  /* ── Search submits ──
   * Typing alone never hits the API: the keyword is only pushed to the
   * backend on Enter / the search button, or the moment the box is cleared. */
  const submitGovSearch = (value: string) => {
    const next = value.trim();
    if (next === govKeyword) return;
    setGovKeyword(next);
    setGovPage(1);
  };

  const submitEvSearch = (value: string) => {
    const next = value.trim();
    if (next === evKeyword) return;
    setEvKeyword(next);
    setEvPage(1);
  };

  useEffect(() => {
    if (activeTab === "governance") {
      fetchGovDocuments();
    } else {
      fetchEvidence();
    }
  }, [activeTab, fetchGovDocuments, fetchEvidence]);

  /* ── Fetch governance document detail ── */
  const fetchGovDocDetail = useCallback(async (id: string) => {
    setGovDetailLoading(true);
    setHistoryLoading(true);
    try {
      const [docRes, historyRes] = await Promise.all([
        getGovernanceDocument(id),
        getGovernanceDocumentHistory(id),
      ]);
      setSelectedGovDoc(docRes.data);
      setDocHistory(historyRes.data ?? []);
    } catch {
      showToast("error", "Failed to load document details");
      setSelectedGovDoc(null);
      setDocHistory([]);
    } finally {
      setGovDetailLoading(false);
      setHistoryLoading(false);
    }
  }, []);

  /* ── Fetch evidence detail ── */
  const fetchEvidenceDetail = useCallback(async (id: string) => {
    setEvidenceDetailLoading(true);
    try {
      const res = await getSupportingEvidence(id);
      setSelectedEvidence(res.data);
    } catch {
      showToast("error", "Failed to load evidence details");
      setSelectedEvidence(null);
    } finally {
      setEvidenceDetailLoading(false);
    }
  }, []);

  /* ── Handlers ── */
  const handleSelectGovDoc = (row: GovDocRow) => {
    fetchGovDocDetail(row.id);
  };

  const handleSelectEvidence = (row: EvidenceRow) => {
    fetchEvidenceDetail(row.id);
  };

  const handleBack = () => {
    setSelectedGovDoc(null);
    setSelectedEvidence(null);
    setEditMode(false);
    setEditEvidenceMode(false);
  };

  /* ── Load document from URL parameter on mount ── */
  useEffect(() => {
    if (!docIdFromUrl) return;

    let cancelled = false;

    const loadDocFromUrl = async () => {
      if (activeTab === "governance") {
        // For governance docs
        const existingDoc = documents.find((d) => d.id === docIdFromUrl);
        if (existingDoc && !cancelled) {
          fetchGovDocDetail(docIdFromUrl);
        } else if (!cancelled) {
          // Directly fetch if not in list yet
          fetchGovDocDetail(docIdFromUrl);
        }
      } else {
        // For evidence
        const existingEv = evidence.find((e) => e.id === docIdFromUrl);
        if (existingEv && !cancelled) {
          fetchEvidenceDetail(docIdFromUrl);
        } else if (!cancelled) {
          // Directly fetch if not in list yet
          fetchEvidenceDetail(docIdFromUrl);
        }
      }
    };

    loadDocFromUrl();
    return () => {
      cancelled = true;
    };
  }, [
    docIdFromUrl,
    documents,
    evidence,
    activeTab,
    fetchGovDocDetail,
    fetchEvidenceDetail,
  ]);

  /* ── Create Governance Doc handlers ── */
  const handleCreateGovStep1 = () => {
    if (!newGovTitle.trim()) return;
    if (!newGovType) return;
    if (!newGovReviewCycle) return;
    if (
      newGovReviewCycle === "custom" &&
      (!newGovCustomDays || parseInt(newGovCustomDays, 10) <= 0)
    ) {
      showToast("error", "Please enter a valid number of days");
      return;
    }
    setCreateGovStep("upload");
  };

  /** Step 2 — Validate the picked file's size before accepting it. */
  const handleCreateGovFileSelect = (file: File | null) => {
    if (file && file.size > MAX_FILE_BYTES) {
      showToast(
        "error",
        `File is too large (${formatFileSize(file.size)}). Maximum allowed size is 25 MB.`,
      );
      setCreateGovFile(null);
      if (createGovFileInputRef.current) {
        createGovFileInputRef.current.value = "";
      }
      return;
    }
    setCreateGovFile(file);
  };

  /**
   * Step 2 — Validate the picked file against the org's active rules.
   * Creates the DRAFT doc on the first call (so we have an id to validate
   * against), then runs /semantic-validation. Reuses the same docId on
   * subsequent "Choose Different File" → re-validate cycles.
   */
  const handleCreateGovValidate = async () => {
    if (!createGovFile) return;

    setCreateGovValidationError(null);

    let docId = createdGovDocId;

    // (a) Create the doc on first attempt
    if (!docId) {
      const requiresReview =
        newGovReviewCycle !== "none" && newGovReviewCycle !== "";
      const reviewCycleDays =
        newGovReviewCycle === "custom"
          ? parseInt(newGovCustomDays, 10)
          : parseInt(newGovReviewCycle, 10);
      setCreateGovStep2("CREATING");
      try {
        const res = await createGovernanceDocument({
          documentType: newGovType as GovernanceDocumentType,
          title: newGovTitle.trim(),
          description: newGovDescription.trim() || undefined,
          owningOrgUnitId: newGovOrgId || undefined,
          requiresReview,
          reviewCycleDays: requiresReview ? reviewCycleDays : undefined,
          reviewAlertDays: requiresReview ? 15 : undefined,
        });
        docId = res.data?.id ?? null;
        if (!docId) throw new Error("Document creation returned no id.");
        setCreatedGovDocId(docId);
        // The store owns the draft from here on, so the keepalive DELETE still
        // fires if the user closes the tab from another route.
        setOrphanDoc(docId);
      } catch (err: unknown) {
        setCreateGovValidationError({
          title: "Couldn't create document",
          message:
            err instanceof Error ? err.message : "Document creation failed.",
        });
        setCreateGovStep2("ERROR");
        return;
      }
    }

    /* (b) Hand the run to the job store. It owns the request, the console and
       the upload from here, so minimising or navigating away no longer kills
       it; we pick the thread back up in the completion / reopen effects. */
    setCreateGovStep2("PICKING");
    void startJob({
      mode: "create",
      docId,
      file: createGovFile,
      documentTitle: newGovTitle.trim() || undefined,
      documentType: newGovType ? formatDocType(newGovType) : undefined,
      fileName: createGovFile.name,
      fileSize: formatFileSize(createGovFile.size),
    });
  };

  /** Step 2 — Footer per substate. */
  const renderCreateGovStep2Footer = () => {
    const closeAll = dismissCreateGovWizard;
    switch (createGovStep2) {
      case "PICKING":
        return (
          <div className="flex w-full items-center justify-end gap-2">
            <button
              onClick={closeAll}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateGovValidate}
              disabled={!createGovFile}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20 disabled:opacity-40"
            >
              Start Validation
            </button>
          </div>
        );
      case "UPLOADING":
        return (
          <div className="flex w-full justify-end">
            <button
              disabled
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 opacity-40 cursor-not-allowed"
            >
              Uploading…
            </button>
          </div>
        );
      case "CREATING":
        return (
          <div className="flex w-full justify-end">
            <button
              disabled
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 opacity-40 cursor-not-allowed"
            >

              Creating document…
            </button>
          </div>
        );
      case "ERROR":
        /* An upload error surfaced inline in the picker (e.g. duplicate
           file). The user can bail out, or clear the error and pick a
           different file - the draft doc + step-2 substate are kept so
           the retry uploads to the same doc without recreating it. */
        return (
          <div className="flex w-full items-center justify-end gap-2">
            <button
              onClick={closeAll}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setCreateGovValidationError(null);
                setCreateGovFile(null);
                if (createGovFileInputRef.current) {
                  createGovFileInputRef.current.value = "";
                }
                setCreateGovStep2("PICKING");
              }}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20"
            >
              Choose Another File
            </button>
          </div>
        );
    }
  };

  const handleSkipGovUpload = () => {
    setShowCreateGov(false);
    resetCreateGovForm();
  };

  const resetCreateGovForm = () => {
    setNewGovTitle("");
    setNewGovDescription("");
    setNewGovType("");
    setNewGovReviewCycle("");
    setNewGovCustomDays("");
    setCreateGovFile(null);
    setCreateGovStep("details");
    setCreatedGovDocId(null);
    setNewGovOrgId("");
    setCreateGovStep2("PICKING");
    setCreateGovValidationError(null);
    clearJob();
  };

  /**
   * Close the create wizard as an explicit user dismissal (Cancel / X / etc).
   * If a DRAFT was created in this session but never successfully uploaded,
   * fire a keepalive DELETE so we don't leave an orphan in the DB.
   */
  const dismissCreateGovWizard = () => {
    flushOrphanDoc();
    setShowCreateGov(false);
    resetCreateGovForm();
  };

  const fetchAccessibleOrgs = async (module: string, action: string) => {
    setAccessibleOrgsLoading(true);
    try {
      const res = await getAPI(
        `${API_URL.ACCESSIBLE_ORGS}?module=${module}&action=${action}&org_type=ORGANIZATION`,
      );
      const orgs = res?.data?.organizations ?? [];
      setAccessibleOrgs(orgs);
      if (orgs.length === 1) {
        setNewGovOrgId(orgs[0].id);
        setNewEvidenceOrgId(orgs[0].id);
      }
    } catch {
      setAccessibleOrgs([]);
    } finally {
      setAccessibleOrgsLoading(false);
    }
  };

  /* ── Create Evidence handlers ── */
  const handleCreateEvidenceStep1 = () => {
    if (!newEvidenceTitle.trim() || getTitleValidationError(newEvidenceTitle))
      return;
    if (!newEvidenceReviewCycle) return;
    if (
      newEvidenceReviewCycle === "custom" &&
      (!newEvidenceCustomDays ||
        parseInt(newEvidenceCustomDays, 10) < 1 ||
        parseInt(newEvidenceCustomDays, 10) > DAYS_MAX)
    ) {
      return;
    }
    setCreateEvidenceStep("upload");
  };

  const handleCreateEvidenceUpload = async () => {
    if (!createEvidenceFile) return;
    setUploadingCreateEvidenceFile(true);
    try {
      const tags = newEvidenceTags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      const requiresReview =
        newEvidenceReviewCycle !== "none" && newEvidenceReviewCycle !== "";
      const reviewCycleDays =
        newEvidenceReviewCycle === "custom"
          ? parseInt(newEvidenceCustomDays, 10)
          : parseInt(newEvidenceReviewCycle, 10);
      const res = await createSupportingEvidence({
        title: newEvidenceTitle.trim(),
        description: newEvidenceDescription.trim() || undefined,
        tags: tags.length > 0 ? tags : undefined,
        owningOrgUnitId: newEvidenceOrgId || undefined,
        requiresReview,
        reviewCycleDays: requiresReview ? reviewCycleDays : undefined,
        reviewAlertDays: requiresReview ? 15 : undefined,
      });
      const evidenceId = res.data?.id;
      if (evidenceId) {
        await uploadSupportingEvidenceFile(evidenceId, createEvidenceFile);
        // The upload endpoint overwrites the evidence title with the uploaded
        // file name, so re-assert the user-entered title afterwards.
        await updateSupportingEvidence(evidenceId, {
          title: newEvidenceTitle.trim(),
        });
      }
      showToast("success", "Evidence created and file uploaded successfully");
      setShowCreateEvidence(false);
      resetCreateEvidenceForm();
      fetchEvidence();
    } catch (err: unknown) {
      if (err instanceof TypeError && err.message === "Failed to fetch") {
        showToast(
          "error",
          "The request was sent but the server never responded. Try again, or try a smaller file.",
          "No Response From Server",
        );
      } else {
        showToast(
          "error",
          err instanceof Error ? err.message : "Failed to create evidence",
          err instanceof Error && err.name !== "Error" ? err.name : undefined,
        );
      }
    } finally {
      setUploadingCreateEvidenceFile(false);
    }
  };

  const handleSkipEvidenceUpload = () => {
    setShowCreateEvidence(false);
    resetCreateEvidenceForm();
  };

  const resetCreateEvidenceForm = () => {
    setNewEvidenceTitle("");
    setNewEvidenceDescription("");
    setNewEvidenceTags("");
    setCreateEvidenceFile(null);
    setCreateEvidenceStep("details");
    setCreatedEvidenceId(null);
    setNewEvidenceOrgId("");
    setNewEvidenceReviewCycle("");
    setNewEvidenceCustomDays("");
  };

  /* ── Tag autocomplete ── */
  const handleTagInputChange = async (value: string) => {
    setNewEvidenceTags(value);
    const lastTag = value.split(",").pop()?.trim();
    if (lastTag && lastTag.length >= 2) {
      try {
        const res = await searchEvidenceTags(lastTag);
        setTagSuggestions(res.data ?? []);
      } catch {
        setTagSuggestions([]);
      }
    } else {
      setTagSuggestions([]);
    }
  };

  /*
   * Orphan-DRAFT cleanup now lives in `processingJobStore`, which outlives
   * this page. Leaving the route no longer deletes the draft - that is what
   * lets a minimised validation run keep going while the user works
   * elsewhere. The store still fires the keepalive DELETE on tab close and on
   * every explicit dismissal.
   */

  /**
   * A background run ended. Refresh whatever it touched and put the wizard
   * away; the store has already shown the toast.
   */
  const seenCompletionRef = React.useRef(jobCompletionTick);
  // Read through a ref so the outcome effect doesn't re-run on selection.
  const selectedGovDocIdRef = React.useRef<string | null>(null);
  selectedGovDocIdRef.current = selectedGovDoc?.id ?? null;
  useEffect(() => {
    if (seenCompletionRef.current === jobCompletionTick) return;
    seenCompletionRef.current = jobCompletionTick;
    const outcome = useProcessingJobStore.getState().lastOutcome;
    setShowCreateGov(false);
    setShowUploadModal(false);
    resetCreateGovForm();
    fetchGovDocuments();
    if (outcome?.kind === "discarded") {
      // The backend soft-deleted the document; don't keep it selected.
      setSelectedGovDoc(null);
      return;
    }
    if (outcome?.kind === "uploaded" && selectedGovDocIdRef.current) {
      fetchGovDocDetail(selectedGovDocIdRef.current);
    }
  }, [jobCompletionTick]);

  /**
   * A run was cancelled or failed. Bring the matching file picker back, with
   * the reason if there was one, so the user can retry without starting over.
   */
  const seenReopenRef = React.useRef(jobReopenPickerTick);
  useEffect(() => {
    if (seenReopenRef.current === jobReopenPickerTick) return;
    seenReopenRef.current = jobReopenPickerTick;
    const target = useProcessingJobStore.getState().reopenTarget;
    if (!target) return;
    if (target.mode === "version") {
      // The user may have navigated away and come back, so make sure the
      // document the run belonged to is the one selected behind the picker.
      setUploadTargetType("governance");
      if (selectedGovDocIdRef.current !== target.docId) {
        fetchGovDocDetail(target.docId);
      }
      setShowUploadModal(true);
      return;
    }
    setShowCreateGov(true);
    setCreateGovStep("upload");
    setCreateGovFile(null);
    if (createGovFileInputRef.current) {
      createGovFileInputRef.current.value = "";
    }
    setCreateGovValidationError(target.error);
    setCreateGovStep2(target.error ? "ERROR" : "PICKING");
  }, [jobReopenPickerTick]);

  /* ── Auto-enter edit mode when navigating from table edit action ── */
  useEffect(() => {
    if (pendingEditRef.current && selectedGovDoc && !govDetailLoading) {
      pendingEditRef.current = false;
      setEditTitle(selectedGovDoc.title);
      setEditDescription(selectedGovDoc.description ?? "");
      if (!selectedGovDoc.requiresReview) {
        setEditReviewCycle("none");
        setEditCustomDays("");
      } else {
        const days = selectedGovDoc.reviewCycleDays;
        if (days === 90) setEditReviewCycle("90");
        else if (days === 180) setEditReviewCycle("180");
        else if (days === 365) setEditReviewCycle("365");
        else {
          setEditReviewCycle("custom");
          setEditCustomDays(String(days ?? ""));
        }
      }
      setEditMode(true);
    }
  }, [selectedGovDoc, govDetailLoading]);

  /* ── Edit Governance Doc handlers ── */
  const enterEditMode = () => {
    if (!selectedGovDoc) return;
    setEditTitle(selectedGovDoc.title);
    setEditDescription(selectedGovDoc.description ?? "");
    // Convert reviewCycleDays to dropdown format
    if (!selectedGovDoc.requiresReview) {
      setEditReviewCycle("none");
      setEditCustomDays("");
    } else {
      const days = selectedGovDoc.reviewCycleDays;
      if (days === 90) setEditReviewCycle("90");
      else if (days === 180) setEditReviewCycle("180");
      else if (days === 365) setEditReviewCycle("365");
      else {
        setEditReviewCycle("custom");
        setEditCustomDays(String(days ?? ""));
      }
    }
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedGovDoc) return;
    if (
      editReviewCycle === "custom" &&
      (!editCustomDays || parseInt(editCustomDays, 10) <= 0)
    ) {
      showToast("error", "Please enter a valid number of days");
      return;
    }
    const requiresReview = editReviewCycle !== "none";
    const reviewCycleDays =
      editReviewCycle === "custom"
        ? parseInt(editCustomDays, 10)
        : parseInt(editReviewCycle, 10);
    setSavingEdit(true);
    try {
      await updateGovernanceDocument(selectedGovDoc.id, {
        title: editTitle.trim() || undefined,
        description: editDescription.trim() || undefined,
        requiresReview,
        reviewCycleDays: requiresReview ? reviewCycleDays : undefined,
      });
      showToast("success", "Document updated");
      setEditMode(false);
      fetchGovDocDetail(selectedGovDoc.id);
      fetchGovDocuments();
    } catch (err: unknown) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to update document",
      );
    } finally {
      setSavingEdit(false);
    }
  };

  /* ── Edit Evidence handlers ── */
  const enterEditEvidenceMode = () => {
    if (!selectedEvidence) return;
    setEditEvidenceTitle(selectedEvidence.title);
    setEditEvidenceDescription(selectedEvidence.description ?? "");
    setEditEvidenceTags(selectedEvidence.tags.join(", "));
    setEditEvidenceMode(true);
  };

  const handleSaveEvidenceEdit = async () => {
    if (!selectedEvidence) return;
    setSavingEvidenceEdit(true);
    try {
      const tags = editEvidenceTags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      await updateSupportingEvidence(selectedEvidence.id, {
        title: editEvidenceTitle.trim() || undefined,
        description: editEvidenceDescription.trim() || undefined,
        tags,
      });
      showToast("success", "Evidence updated");
      setEditEvidenceMode(false);
      fetchEvidenceDetail(selectedEvidence.id);
      fetchEvidence();
    } catch (err: unknown) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to update evidence",
      );
    } finally {
      setSavingEvidenceEdit(false);
    }
  };

  /* ── Edit Evidence from table ── */
  const openEditEvidenceModal = (row: EvidenceRow) => {
    setEditingEvidenceId(row.id);
    setEditEvidenceTitle(row.title);
    setEditEvidenceDescription(row.description);
    setEditEvidenceTags(row.tags.join(", "));
    setShowEditEvidenceModal(true);
  };

  const handleSaveEvidenceFromModal = async () => {
    if (!editingEvidenceId) return;
    setSavingEvidenceEdit(true);
    try {
      const tags = editEvidenceTags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      await updateSupportingEvidence(editingEvidenceId, {
        title: editEvidenceTitle.trim() || undefined,
        description: editEvidenceDescription.trim() || undefined,
        tags,
      });
      showToast("success", "Evidence updated");
      setShowEditEvidenceModal(false);
      setEditingEvidenceId(null);
      fetchEvidence();
    } catch (err: unknown) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to update evidence",
      );
    } finally {
      setSavingEvidenceEdit(false);
    }
  };

  /* ── Delete handlers ── */
  const handleDeleteGovDoc = (id: string) => {
    setDeleteTargetId(id);
    setDeleteTargetType("governance");
    setDeleteModalOpen(true);
  };

  const handleDeleteEvidence = (id: string) => {
    setDeleteTargetId(id);
    setDeleteTargetType("evidence");
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    setDeleteModalOpen(false);
    setDeleting(deleteTargetId);
    try {
      if (deleteTargetType === "governance") {
        await deleteGovernanceDocument(deleteTargetId);
        showToast("success", "Document deleted");
        fetchGovDocuments();
        if (selectedGovDoc?.id === deleteTargetId) {
          setSelectedGovDoc(null);
        }
      } else {
        await deleteSupportingEvidence(deleteTargetId);
        showToast("success", "Evidence deleted");
        fetchEvidence();
        if (selectedEvidence?.id === deleteTargetId) {
          setSelectedEvidence(null);
        }
      }
    } catch (err: unknown) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to delete",
      );
    } finally {
      setDeleting(null);
      setDeleteTargetId(null);
    }
  };

  /* ── Lifecycle actions ── */
  const openLifecycleModal = (action: string) => {
    setLifecycleAction(action);
    setLifecycleReason("");
    setShowLifecycleModal(true);
  };

  const handleLifecycleAction = async () => {
    if (!selectedGovDoc) return;
    setLifecycleProcessing(true);
    try {
      switch (lifecycleAction) {
        case "submit":
          await submitGovernanceDocumentForReview(selectedGovDoc.id);
          showToast("success", "Submitted for review");
          break;
        case "approve":
          await reviewGovernanceDocument(selectedGovDoc.id, "APPROVE");
          showToast("success", "Document approved");
          break;
        case "publish":
          await reviewGovernanceDocument(selectedGovDoc.id, "PUBLISH");
          showToast("success", "Document published");
          break;
        case "reject":
          if (!lifecycleReason.trim()) {
            showToast("error", "Rejection reason is required");
            setLifecycleProcessing(false);
            return;
          }
          await reviewGovernanceDocument(
            selectedGovDoc.id,
            "REJECT",
            lifecycleReason,
          );
          showToast("success", "Document rejected");
          break;
        case "new-version":
          await createNewGovernanceDocumentVersion(selectedGovDoc.id);
          showToast("success", "New version created");
          break;
        case "retire":
          if (!lifecycleReason.trim()) {
            showToast("error", "Retirement reason is required");
            setLifecycleProcessing(false);
            return;
          }
          await retireGovernanceDocument(selectedGovDoc.id, lifecycleReason);
          showToast("success", "Document withdrawn");
          break;
      }
      setShowLifecycleModal(false);
      fetchGovDocDetail(selectedGovDoc.id);
      fetchGovDocuments();
    } catch (err: unknown) {
      showToast("error", err instanceof Error ? err.message : "Action failed");
    } finally {
      setLifecycleProcessing(false);
    }
  };

  /* ── Upload handlers ── */
  const openUploadModal = (type: "governance" | "evidence") => {
    setUploadTargetType(type);
    setUploadFile(null);
    setShowUploadModal(true);
  };

  const handleUploadFile = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      if (uploadTargetType === "governance" && selectedGovDoc) {
        await uploadGovernanceDocumentFile(selectedGovDoc.id, uploadFile);
        showToast("success", "File uploaded successfully");
        fetchGovDocDetail(selectedGovDoc.id);
      } else if (uploadTargetType === "evidence" && selectedEvidence) {
        await uploadSupportingEvidenceFile(selectedEvidence.id, uploadFile);
        showToast("success", "File uploaded successfully");
        fetchEvidenceDetail(selectedEvidence.id);
      }
      setShowUploadModal(false);
      setUploadFile(null);
    } catch (err: unknown) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to upload file",
      );
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = async () => {
    if (!selectedGovDoc) return;
    try {
      await deleteGovernanceDocumentFile(selectedGovDoc.id);
      showToast("success", "File removed successfully");
      fetchGovDocDetail(selectedGovDoc.id);
    } catch (err: unknown) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to delete file",
      );
    }
  };

  /* ── Sort icon components ── */
  const DocSortIcon = ({ col }: { col: string }) => {
    const active = docSortKey === col;
    return (
      <span
        className="inline-flex flex-col items-center ml-2 select-none"
        style={{ fontSize: "7px", lineHeight: "7px" }}
      >
        <span
          className={`font-black ${active && docSortDir === "asc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▲
        </span>
        <span
          className={`font-black ${active && docSortDir === "desc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▼
        </span>
      </span>
    );
  };

  const EvSortIcon = ({ col }: { col: string }) => {
    const active = evSortKey === col;
    return (
      <span
        className="inline-flex flex-col items-center ml-2 select-none"
        style={{ fontSize: "7px", lineHeight: "7px" }}
      >
        <span
          className={`font-black ${active && evSortDir === "asc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▲
        </span>
        <span
          className={`font-black ${active && evSortDir === "desc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▼
        </span>
      </span>
    );
  };

  /* ── Rows for the current page ──
   * No client-side filtering, sorting or slicing: the backend already applied
   * the keyword, sort column/order, filters and page window. */
  const paginatedDocs = documents;
  const paginatedEvidence = evidence;
  /** Serial numbers continue across pages (page 2 with 10/page starts at 11). */
  const govRowSerial = (index: number) => (govPage - 1) * govPageSize + index + 1;
  const evRowSerial = (index: number) => (evPage - 1) * evPageSize + index + 1;

  const isGovDocEditable = selectedGovDoc?.status === "DRAFT";

  /* ── Loading skeleton while fetching detail (selection kept in state) ── */
  if (
    (govDetailLoading && !selectedGovDoc && activeTab === "governance") ||
    (evidenceDetailLoading && !selectedEvidence && activeTab === "evidence")
  ) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 animate-pulse">
          <div className="h-3.5 w-16 rounded bg-muted" />
          <div className="h-3.5 w-3 rounded bg-muted" />
          <div className="h-3.5 w-32 rounded bg-muted" />
        </div>
        <SkeletonCard lines={6} />
        <SkeletonCard lines={4} />
      </div>
    );
  }

  /* ── Main content with conditional rendering ── */
  const mainContent = selectedGovDoc ? (
    <GovernanceDocumentDetail
      doc={selectedGovDoc}
      loading={govDetailLoading}
      editMode={editMode}
      editTitle={editTitle}
      editDescription={editDescription}
      editReviewCycle={editReviewCycle}
      editCustomDays={editCustomDays}
      savingEdit={savingEdit}
      onBack={handleBack}
      onEdit={enterEditMode}
      onCancelEdit={() => setEditMode(false)}
      onSaveEdit={handleSaveEdit}
      onEditTitleChange={setEditTitle}
      onEditDescriptionChange={setEditDescription}
      onEditReviewCycleChange={setEditReviewCycle}
      onEditCustomDaysChange={setEditCustomDays}
      onLifecycleAction={openLifecycleModal}
      onUpload={() => openUploadModal("governance")}
      onRemoveFile={handleRemoveFile}
      onDelete={() => handleDeleteGovDoc(selectedGovDoc.id)}
      isEditable={isGovDocEditable}
      history={docHistory}
      historyLoading={historyLoading}
    />
  ) : selectedEvidence ? (
    <EvidenceDetail
      evidence={selectedEvidence}
      loading={evidenceDetailLoading}
      editMode={editEvidenceMode}
      editTitle={editEvidenceTitle}
      editDescription={editEvidenceDescription}
      editTags={editEvidenceTags}
      savingEdit={savingEvidenceEdit}
      onBack={handleBack}
      onEdit={enterEditEvidenceMode}
      onCancelEdit={() => setEditEvidenceMode(false)}
      onSaveEdit={handleSaveEvidenceEdit}
      onEditTitleChange={setEditEvidenceTitle}
      onEditDescriptionChange={setEditEvidenceDescription}
      onEditTagsChange={setEditEvidenceTags}
      onUpload={() => openUploadModal("evidence")}
      onDelete={() => handleDeleteEvidence(selectedEvidence.id)}
    />
  ) : (
    <div className="flex flex-col flex-1 min-h-0 gap-6">
      {/* Page header */}
      <div className="flex-shrink-0 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1>Content Registry</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage governance documents and supporting evidence
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0">
          {activeTab === "governance" ? (
            <RBACAction module="document" action="create">
              <button
                onClick={() => {
                  resetCreateGovForm();
                  fetchAccessibleOrgs("document", "create");
                  setShowCreateGov(true);
                }}
                className="h-9 flex items-center gap-2 px-4 rounded-xl bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-all shadow-[0_4px_14px_rgba(79,70,229,0.3)] dark:shadow-[0_4px_14px_rgba(139,92,246,0.3)] hover:-translate-y-px"
              >
                <Plus className="size-4" /> New Document
              </button>
            </RBACAction>
          ) : (
            <RBACAction module="evidence" action="create">
              <button
                onClick={() => {
                  resetCreateEvidenceForm();
                  fetchAccessibleOrgs("evidence", "create");
                  setShowCreateEvidence(true);
                }}
                className="h-9 flex items-center gap-2 px-4 rounded-xl bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-all shadow-[0_4px_14px_rgba(79,70,229,0.3)] dark:shadow-[0_4px_14px_rgba(139,92,246,0.3)] hover:-translate-y-px"
              >
                <Plus className="size-4" /> New Evidence
              </button>
            </RBACAction>
          )}
        </div>
      </div>

      {/* Main tabs: Governance vs Evidence — same accent styling as the Catalog page tabs */}
      <div className="flex-shrink-0 inline-flex items-center gap-2 p-1 rounded-xl bg-[#f1f5f9] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.06] w-fit">
        <button
          type="button"
          onClick={() => {
            setActiveTab("governance");
            setFilterType("");
            setFilterStatus("");
            setGovPage(1);
            setGovPageSize(getPageSizeOptions("card")[0]);
          }}
          className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-sm font-semibold transition-all ${activeTab === "governance"
              ? "bg-card text-indigo-600 dark:text-indigo-400 shadow-sm"
              : "text-[#64748b] dark:text-white/60 hover:text-foreground"
            }`}
        >
          <FolderOpen className="size-4" />
          {TAB_TITLE.governance}
        </button>
        <button
          type="button"
          onClick={() => {
            setActiveTab("evidence");
            setFilterType("");
            setFilterStatus("");
            setEvPage(1);
            setEvPageSize(getPageSizeOptions("card")[0]);
          }}
          className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-sm font-semibold transition-all ${activeTab === "evidence"
              ? "bg-card text-indigo-600 dark:text-indigo-400 shadow-sm"
              : "text-[#64748b] dark:text-white/60 hover:text-foreground"
            }`}
        >
          <FilePlus className="size-4" />
          {TAB_TITLE.evidence}
        </button>
      </div>

      {activeTab === "governance" ? (
        <>
          {/* Error banner */}
          {govError && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400">
              <AlertTriangle className="size-4 flex-shrink-0" />
              <span>{govError}</span>
              <button
                onClick={fetchGovDocuments}
                className="ml-auto text-xs underline hover:no-underline"
              >
                Retry
              </button>
            </div>
          )}

          <EntityCardGrid<GovDocRow>
            items={paginatedDocs}
            loading={govLoading}
            getKey={(r) => r.id}
            getStatus={(row) => mapLifecycleToVariant(row.status)}
            onOpen={handleSelectGovDoc}
            page={govPage - 1}
            pageSize={govPageSize}
            totalItems={govTotal}
            onPageChange={(p) => setGovPage(p + 1)}
            onPageSizeChange={(s) => {
              setGovPageSize(s);
              setGovPage(1);
            }}
            pageSizeOptions={[...getPageSizeOptions(govViewMode)]}
            itemLabel="documents"
            emptyText="No governance documents found"
            viewMode={govViewMode}
            onViewModeChange={handleGovViewModeChange}
            toolbarLeft={
              <TableToolbar
                search={{
                  value: govSearch,
                  onChange: setGovSearch,
                  onSubmit: submitGovSearch,
                  placeholder: "Search Documents",
                  ariaLabel: "Search documents",
                }}
                filters={[
                  {
                    key: "type",
                    value: filterType,
                    // A narrower filter always restarts at page 1.
                    onChange: (v) => {
                      setFilterType(v as GovernanceDocumentType | "");
              <>
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground/50 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search documents"
                    value={govSearch}
                    onChange={(e) => {
                      setGovSearch(e.target.value);
                      setGovPage(1);
                    }}
                    className="w-full pl-9 pr-3 py-2 rounded-xl border border-border bg-card text-sm placeholder:text-muted-foreground/50 outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all"
                  />
                </div>
                <Dropdown
                  size="sm"
                  variant="pill"
                  value={filterType}
                  onChange={(v) => setFilterType(v as GovernanceDocumentType | "")}
                  options={[
                    { value: "", label: "All Types" },
                    ...DOCUMENT_TYPES.map((t) => ({
                      value: t.value,
                      label: t.label,
                    })),
                  ]}
                />
                <Dropdown
                  size="sm"
                  variant="pill"
                  value={filterStatus}
                  onChange={(v) =>
                    setFilterStatus(v as DocumentLifecycleStatus | "")
                  }
                  options={[
                    { value: "", label: "All Status" },
                    ...LIFECYCLE_STATUSES.map((s) => ({
                      value: s.value,
                      label: s.label,
                      dot:
                        s.value === "PUBLISHED"
                          ? "bg-emerald-500"
                          : s.value === "IN_REVIEW"
                            ? "bg-blue-500"
                            : s.value === "DRAFT"
                              ? "bg-slate-400"
                              : undefined,
                    })),
                  ]}
                />
              </>
                    },
                    options: [
                      { value: "", label: "All Types" },
                      ...DOCUMENT_TYPES.map((t) => ({
                        value: t.value,
                        label: t.label,
                      })),
                    ],
                  },
                  {
                    key: "status",
                    value: filterStatus,
                    onChange: (v) => {
                      setFilterStatus(v as DocumentLifecycleStatus | "");
                      setGovPage(1);
                    },
                    options: [
                      { value: "", label: "All Status" },
                      ...LIFECYCLE_STATUSES.map((s) => ({
                        value: s.value,
                        label: s.label,
                        dot:
                          s.value === "PUBLISHED"
                            ? "bg-emerald-500"
                            : s.value === "IN_REVIEW"
                              ? "bg-blue-500"
                              : s.value === "DRAFT"
                                ? "bg-slate-400"
                                : undefined,
                      })),
                    ],
                  },
                ]}
                trailing={
                  (filterType || filterStatus) && (
                    <button
                      onClick={() => {
                        setFilterType("");
                        setFilterStatus("");
                        setGovPage(1);
                      }}
                      className="h-[38px] flex items-center rounded-lg border border-border bg-card px-3 text-sm text-muted-foreground transition-colors hover:border-indigo-400/60 hover:text-foreground dark:hover:border-indigo-400/40"
                    >
                      Clear
                    </button>
                  )
                }
              />
            }
            renderTitle={(row) => (
              <TitleTooltip text={row.title}>
                <h3 className="text-[13px] font-bold leading-snug tracking-[-0.01em] text-foreground truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                  {row.title}
                </h3>
              </TitleTooltip>
            )}
            renderHeaderRight={() => (
              <FileText className="size-4 text-indigo-500 dark:text-indigo-400" />
            )}
            renderBadges={(row) => (
              <>
                <span
                  className={`inline-flex items-center justify-center px-2 py-0.5 rounded-[4px] text-[10px] font-bold ${DOC_TYPE_COLORS[row.documentType] ?? "bg-[#f1f5f9] dark:bg-white/[0.05] text-[#475569] dark:text-white/60"}`}
                >
                  {formatDocType(row.documentType)}
                </span>
                <VersionChip version={row.currentVersion} />
              </>
            )}
            renderDates={(row) => (
              <MetaDates created={row.createdAt} updated={row.updatedAt} />
            )}
            renderFooterActions={(row) => (
              <div
                className="flex items-center gap-1 flex-shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                {row.hasFile ? (
                  <>
                    <Tip label="Preview">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openTablePreview(row.id);
                        }}
                        className="p-1.5 rounded-md hover:bg-blue-50 dark:hover:bg-blue-500/10 text-[#64748b] dark:text-white/60 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      >
                        <Eye className="size-4" />
                      </button>
                    </Tip>
                    <Tip label="Download">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          triggerDownload(
                            downloadGovernanceDocumentFile(row.id),
                            row.fileName as string | null | undefined,
                          );
                        }}
                        className="p-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-[#64748b] dark:text-white/60 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                      >
                        <Download className="size-4" />
                      </button>
                    </Tip>
                  </>
                ) : (
                  <span className="text-[10px] font-semibold text-[#94a3b8] dark:text-white/40 px-1">
                    No file
                  </span>
                )}
                <Tip label="Delete Governance Document" side="bottom">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteGovDoc(row.id);
                    }}
                    disabled={deleting === row.id}
                    className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-[#64748b] dark:text-white/60 hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                  >
                    {deleting === row.id ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Trash2 className="size-4 hover:text-red-600" />
                    )}
                  </button>
                </Tip>
              </div>
            )}
            renderTable={(rows) => (
              <DataTable
                columns={
                  [
                    ...getGovDocColumns(govPage, govPageSize)
                      .map((col) => {
                        if (col.key === "sno") {
                          return {
                            ...col,
                            render: (_row: GovDocRow, index: number) => (
                              <span className="text-xs font-medium text-muted-foreground">
                                {govRowSerial(index)}
                              </span>
                            ),
                          };
                        }
                        const docSortableLabels: Record<string, string> = {
                          title: "Title",
                          documentType: "Type",
                          currentVersion: "Version",
                          status: "Status",
                          createdAt: "Created On",
                          updatedAt: "Updated On",
                        };
                        if (col.key in docSortableLabels) {
                          return {
                            ...col,
                            header: (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDocSort(col.key);
                                }}
                                className="inline-flex items-center whitespace-nowrap cursor-pointer select-none hover:text-foreground transition-colors"
                              >
                                {docSortableLabels[col.key]}
                                <DocSortIcon col={col.key} />
                              </button>
                            ),
                          };
                        }
                        return col;
                      }),
                    {
                      key: "actions",
                      header: "Action",
                      headerClassName: "text-center",
                      render: (row: GovDocRow) => (
                        <div className="flex items-center justify-center gap-1">
                          {row.hasFile ? (
                            <>
                              <Tip label="Preview">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openTablePreview(row.id);
                                  }}
                                  className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-all"
                                >
                                  <Eye className="size-4" />
                                </button>
                              </Tip>
                              <Tip label="Download">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    triggerDownload(
                                      downloadGovernanceDocumentFile(row.id),
                                      row.fileName as string | null | undefined,
                                    );
                                  }}
                                  className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-all"
                                >
                                  <Download className="size-4" />
                                </button>
                              </Tip>
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground px-1">No file</span>
                          )}
                          <Tip label="Delete Governance Document" side="bottom">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteGovDoc(row.id);
                              }}
                              disabled={deleting === row.id}
                              className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-all disabled:opacity-50"
                            >
                              {deleting === row.id ? (
                                <Loader2 className="size-4 animate-spin" />
                              ) : (
                                <Trash2 className="size-4 hover:text-red-600" />
                              )}
                            </button>
                          </Tip>
                        </div>
                      ),
                    },
                  ] as Column<GovDocRow & Record<string, unknown>>[]
                }
                data={rows}
                rowKey={(r) => r.id}
                onRowClick={handleSelectGovDoc}
                loading={govLoading}
                emptyText="No governance documents found"
                itemLabel="documents"
              />
            )}
          />
        </>
      ) : (
        <>
          {/* Evidence Error banner */}
          {evidenceError && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400">
              <AlertTriangle className="size-4 flex-shrink-0" />
              <span>{evidenceError}</span>
              <button
                onClick={fetchEvidence}
                className="ml-auto text-xs underline hover:no-underline"
              >
                Retry
              </button>
            </div>
          )}

          <EntityCardGrid<EvidenceRow>
            items={paginatedEvidence}
            loading={evidenceLoading}
            getKey={(r) => r.id}
            getStatus={(row) => (row.hasFile ? "published" : "draft")}
            getStatusLabel={(row) => (row.hasFile ? "File attached" : "No file")}
            onOpen={handleSelectEvidence}
            page={evPage - 1}
            pageSize={evPageSize}
            totalItems={evTotal}
            onPageChange={(p) => setEvPage(p + 1)}
            onPageSizeChange={(s) => {
              setEvPageSize(s);
              setEvPage(1);
            }}
            pageSizeOptions={[...getPageSizeOptions(evViewMode)]}
            itemLabel="items"
            emptyText="No supporting evidence found"
            viewMode={evViewMode}
            onViewModeChange={handleEvViewModeChange}
            toolbarLeft={
              <TableToolbar
                search={{
                  value: evidenceSearch,
                  onChange: setEvidenceSearch,
                  onSubmit: submitEvSearch,
                  placeholder: "Search Evidences",
                  ariaLabel: "Search evidence",
                }}
              />
            }
            renderTitle={(row) => (
              <TitleTooltip text={row.title}>
                <h3 className="text-[13px] font-bold leading-snug tracking-[-0.01em] text-foreground truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                  {row.title}
                </h3>
              </TitleTooltip>
            )}
            renderHeaderRight={() => (
              <FolderOpen className="size-4 text-indigo-500 dark:text-indigo-400" />
            )}
            renderBadges={(row) => {
              const tags = row.tags as string[];
              if (!tags || tags.length === 0) return null;
              return (
                <>
                  {tags.slice(0, 3).map((tag) => (
                    <Tip key={tag} label={tag} contentClassName="max-w-[160px] break-words text-[11px]">
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-[10px] font-medium text-indigo-600 dark:text-indigo-400">
                        <Tag className="size-2.5 flex-shrink-0" />
                        <span className="truncate max-w-[80px] block">{tag}</span>
                      </span>
                    </Tip>
                  ))}
                  {tags.length > 3 && (
                    <span className="text-[10px] font-semibold text-[#94a3b8] dark:text-white/40">
                      +{tags.length - 3} more
                    </span>
                  )}
                </>
              );
            }}
            renderDates={(row) => (
              <MetaDates created={row.createdAt} updated={row.updatedAt} />
            )}
            renderFooterActions={(row) => (
              <div
                className="flex items-center gap-1 flex-shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                <Tip label="Edit Evidence" side="bottom">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditEvidenceModal(row);
                    }}
                    className="p-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-[#64748b] dark:text-white/60 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    <Pencil className="size-4 hover:text-indigo-600" />
                  </button>
                </Tip>
                {row.hasFile && (
                  <>
                    <Tip label="Preview">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewEvidenceRow(row);
                        }}
                        className="p-1.5 rounded-md hover:bg-blue-50 dark:hover:bg-blue-500/10 text-[#64748b] dark:text-white/60 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      >
                        <Eye className="size-4" />
                      </button>
                    </Tip>
                    <Tip label="Download">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          triggerDownload(
                            downloadSupportingEvidenceFile(row.id),
                            row.fileName,
                          );
                        }}
                        className="p-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-[#64748b] dark:text-white/60 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                      >
                        <Download className="size-4" />
                      </button>
                    </Tip>
                  </>
                )}
              </div>
            )}
            renderTable={(rows) => (
              <DataTable
                columns={
                  [
                    ...getEvidenceColumns(evPage, evPageSize).map((col) => {
                      if (col.key === "sno") {
                        return {
                          ...col,
                          render: (_row: EvidenceRow, index: number) => (
                            <span className="text-xs font-medium text-muted-foreground">
                              {evRowSerial(index)}
                            </span>
                          ),
                        };
                      }
                      const evSortableLabels: Record<string, string> = {
                        title: "Title",
                        createdAt: "Created On",
                        updatedAt: "Updated On",
                      };
                      if (col.key in evSortableLabels) {
                        return {
                          ...col,
                          header: (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEvSort(col.key);
                              }}
                              className="inline-flex items-center whitespace-nowrap cursor-pointer select-none hover:text-foreground transition-colors"
                            >
                              {evSortableLabels[col.key]}
                              <EvSortIcon col={col.key} />
                            </button>
                          ),
                        };
                      }
                      return col;
                    }),
                    {
                      key: "id",
                      header: "Action",
                      headerClassName: "text-center",
                      render: (row) => (
                        <div className="flex items-center justify-center gap-0.5">
                          <Tip label="Edit Evidence" side="bottom">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditEvidenceModal(row);
                              }}
                              className="p-1.5 rounded-md hover:bg-indigo-50 dark:hover:bg-indigo-500/10 text-muted-foreground hover:text-indigo-600 dark:hover:text-indigo-400 transition-all"
                            >
                              <Pencil className="size-4 hover:text-indigo-600" />
                            </button>
                          </Tip>
                          {row.hasFile && (
                            <>
                              <Tip label="Preview">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPreviewEvidenceRow(row);
                                  }}
                                  className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-all"
                                >
                                  <Eye className="size-4" />
                                </button>
                              </Tip>
                              <Tip label="Download">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    triggerDownload(
                                      downloadSupportingEvidenceFile(row.id),
                                      row.fileName,
                                    );
                                  }}
                                  className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-all"
                                >
                                  <Download className="size-4" />
                                </button>
                              </Tip>
                            </>
                          )}
                        </div>
                      ),
                    },
                  ] as Column<EvidenceRow & Record<string, unknown>>[]
                }
                data={rows}
                rowKey={(r) => r.id}
                onRowClick={handleSelectEvidence}
                loading={evidenceLoading}
                emptyText="No supporting evidence found"
                itemLabel="items"
              />
            )}
          />
        </>
      )}
    </div>
  );

  return (
    <>
      {mainContent}

      {/* ── Table File Preview Modal ── */}
      {tablePreviewDocId && (
        <PreviewModal
          url={`${previewGovernanceDocumentFile(tablePreviewDocId)}?v=${documents.find((d) => d.id === tablePreviewDocId)?.currentVersion ?? 1}&t=${tablePreviewToken}`}
          fileName={null}
          downloadUrl={downloadGovernanceDocumentFile(tablePreviewDocId)}
          onClose={closeTablePreview}
        />
      )}

      {/* ── Create Governance Document Modal (2-step) ──
          Once validation starts, the knowledge processing console takes over
          from `ProcessingJobHost` and this modal steps aside. */}
      <Modal
        open={showCreateGov && !hasBackgroundJob}
        onClose={dismissCreateGovWizard}
        title={createGovStep === "details" ? "Add Document" : "Upload Document"}
        description={
          createGovStep === "details"
            ? "Step 1 of 2 - Document details"
            : "Step 2 of 2 - Attach a file *"
        }
        onBack={
          createGovStep === "upload" && createGovStep2 === "PICKING"
            ? () => { setCreateGovStep("details"); setCreatedGovDocId(null); setOrphanDoc(null); }
            : undefined
        }
        size={createGovStep === "details" ? "lg" : "lg"}
        closable={
          createGovStep2 !== "CREATING" && createGovStep2 !== "UPLOADING"
        }
        footer={
          createGovStep === "details" ? (
            <ModalFooterButtons
              onCancel={dismissCreateGovWizard}
              onSubmit={handleCreateGovStep1}
              submitLabel="Next"
              submitDisabled={
                !newGovTitle.trim() ||
                newGovTitle.trim().length > TITLE_MAX_LENGTH ||
                !newGovType ||
                !newGovReviewCycle ||
                !newGovOrgId ||
                accessibleOrgs.length === 0 ||
                (newGovReviewCycle === "custom" &&
                  (!newGovCustomDays ||
                    parseInt(newGovCustomDays, 10) < 1 ||
                    parseInt(newGovCustomDays, 10) > DAYS_MAX))
              }
            />
          ) : (
            renderCreateGovStep2Footer()
          )
        }
      >
        {createGovStep === "details" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <FormField
                label="Title"
                required
                error={
                  newGovTitle.length > TITLE_MAX_LENGTH
                    ? `Title must be ${TITLE_MAX_LENGTH} characters or fewer`
                    : undefined
                }
                hint={undefined}
              >
                <FormInput
                  value={newGovTitle}
                  onChange={setNewGovTitle}
                  placeholder="e.g. Information Security Policy"
                />
              </FormField>
            </div>
            <FormField label="Document Type" required>
              <FormSelect
                options={DOCUMENT_TYPES}
                value={newGovType}
                onChange={(v) =>
                  setNewGovType(v as GovernanceDocumentType | "")
                }
                placeholder="Select document type"
              />
            </FormField>
            <FormField label="Organization" required>
              {accessibleOrgsLoading ? (
                <div className="flex items-center gap-2 py-2">
                  <Loader2 className="size-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Loading</span>
                </div>
              ) : accessibleOrgs.length === 0 ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200/50 dark:border-red-500/20">
                  <Ban className="size-4 text-red-500 flex-shrink-0" />
                  <span className="text-sm text-red-600 dark:text-red-400">
                    No accessible organizations.
                  </span>
                </div>
              ) : accessibleOrgs.length === 1 ? (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#f5f7fb] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] text-sm">
                  <span className="font-medium">{accessibleOrgs[0].name}</span>
                </div>
              ) : (
                <FormSelect
                  options={accessibleOrgs.map((o) => ({
                    value: o.id,
                    // Name only: the short code is an internal identifier and
                    // the org id is what the backend is given.
                    label: o.name,
                  }))}
                  value={newGovOrgId}
                  onChange={setNewGovOrgId}
                  placeholder="Select organization"
                />
              )}
            </FormField>
            <FormField label="Review Cycle" required>
              <FormSelect
                options={[
                  { value: "none", label: "No review required" },
                  { value: "90", label: "Quarterly" },
                  { value: "180", label: "Half-Yearly" },
                  { value: "365", label: "Annually" },
                  { value: "custom", label: "Custom" },
                ]}
                value={newGovReviewCycle}
                onChange={(v) =>
                  setNewGovReviewCycle(
                    v as "" | "none" | "90" | "180" | "365" | "custom",
                  )
                }
                placeholder="Select review cycle"
              />
            </FormField>
            {newGovReviewCycle === "custom" ? (
              <FormField
                label="Days"
                required
                error={
                  newGovCustomDays &&
                    (parseInt(newGovCustomDays, 10) < 1 ||
                      parseInt(newGovCustomDays, 10) > DAYS_MAX)
                    ? `Must be between 1 and ${DAYS_MAX} days`
                    : undefined
                }
                hint={!newGovCustomDays ? "e.g. 30, 60, 120" : undefined}
              >
                <FormInput
                  type="number"
                  value={newGovCustomDays}
                  onChange={setNewGovCustomDays}
                  placeholder="Enter days"
                  min={1}
                  max={DAYS_MAX}
                />
              </FormField>
            ) : (
              <div />
            )}
            <div className="sm:col-span-2">
              <FormField label="Description">
                <FormTextarea
                  value={newGovDescription}
                  onChange={setNewGovDescription}
                  placeholder="Brief description"
                  rows={3}
                />
              </FormField>
            </div>
          </div>
        ) : createGovStep2 === "PICKING" ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 text-sm text-indigo-700 dark:text-indigo-400">
              <Upload className="size-4 flex-shrink-0" />
              <span className="min-w-0">
                Attach a PDF for{" "}
                <strong
                  className="inline-block max-w-[260px] truncate align-bottom"
                  title={newGovTitle}
                >
                  "{newGovTitle}"
                </strong>
                . We'll check it against the org's active rules before saving.
              </span>
            </div>
            <div>
              <label
                className={`flex flex-col items-center justify-center gap-2 px-4 ${createGovFile ? "py-3" : "py-10"} rounded-xl border-2 border-dashed transition-all ${createGovFile ? "border-emerald-400 dark:border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-500/[0.04]" : "border-indigo-300 dark:border-indigo-500/30 hover:border-indigo-400 cursor-pointer"}`}
              >
                {createGovFile ? (
                  <div className="flex items-center justify-between w-full px-3 py-2 rounded-lg bg-emerald-100/50 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/20 text-[12px]">
                    <div className="flex items-start gap-2 min-w-0">
                      <FileText className="size-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div className="flex flex-col min-w-0">
                        <span className="text-emerald-800 dark:text-emerald-300 font-medium break-all">
                          {createGovFile.name}
                        </span>
                        <span className="text-[11px] text-emerald-600/40 dark:text-emerald-400/40">
                          {formatFileSize(createGovFile.size)}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCreateGovFile(null);
                        if (createGovFileInputRef.current)
                          createGovFileInputRef.current.value = "";
                      }}
                      className="p-1 rounded text-emerald-400 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="size-6 text-indigo-400 dark:text-indigo-300" />
                    <span className="text-sm font-semibold text-indigo-400 dark:text-indigo-300">
                      Click to select a  document
                    </span>
                    <span className="text-xs font-medium text-slate-300 dark:text-white/80">
                      PDF · max 25 MB
                    </span>
                  </>
                )}
                <input
                  ref={createGovFileInputRef}
                  type="file"
                  accept="application/pdf,.pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx"
                  className="hidden"
                  onChange={(e) =>
                    handleCreateGovFileSelect(e.target.files?.[0] ?? null)
                  }
                />
              </label>
            </div>
          </div>
        ) : createGovStep2 === "CREATING" ? (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 className="size-8 animate-spin text-indigo-500" />
            <div className="text-center space-y-1">
              <p className="text-sm font-medium text-foreground">
                Creating document…
              </p>
              <p className="text-xs text-muted-foreground">
                Setting up the DRAFT before validation runs.
              </p>
            </div>
          </div>
        ) : createGovStep2 === "UPLOADING" ? (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 className="size-8 animate-spin text-indigo-500" />
            <div className="text-center space-y-1">
              <p className="text-sm font-medium text-foreground">Uploading…</p>
              {createGovFile && (
                <p className="text-xs text-muted-foreground">
                  <FileText className="inline size-3 mr-1 -mt-0.5" />
                  {createGovFile.name}
                </p>
              )}
            </div>
          </div>
        ) : createGovStep2 === "ERROR" ? (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20">
            <AlertTriangle className="size-5 flex-shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
            <div className="space-y-0.5 min-w-0">
              <p className="text-sm font-semibold text-red-700 dark:text-red-400">
                {createGovValidationError?.title ?? "Error"}
              </p>
              <p className="text-sm text-red-700/90 dark:text-red-400/90">
                {createGovValidationError?.message ?? "Something went wrong."}
              </p>
            </div>
          </div>
        ) : (
          <div />
        )}
      </Modal>

      {/* ── Create Evidence Modal (2-step) ── */}
      <Modal
        open={showCreateEvidence}
        onClose={() => {
          setShowCreateEvidence(false);
          resetCreateEvidenceForm();
        }}
        title={
          createEvidenceStep === "details" ? "Add Evidence" : "Upload File"
        }
        description={
          createEvidenceStep === "details"
            ? "Step 1 of 2 - Evidence details"
            : "Step 2 of 2 - Attach a file (required)"
        }
        size={createEvidenceStep === "details" ? "lg" : "md"}
        closable={!creatingEvidence && !uploadingCreateEvidenceFile}
        footer={
          createEvidenceStep === "details" ? (
            <ModalFooterButtons
              onCancel={() => {
                setShowCreateEvidence(false);
                resetCreateEvidenceForm();
              }}
              onSubmit={handleCreateEvidenceStep1}
              submitLabel="Next"
              submitDisabled={
                creatingEvidence ||
                !newEvidenceTitle.trim() ||
                !!getTitleValidationError(newEvidenceTitle) ||
                !newEvidenceTags.trim() ||
                !newEvidenceOrgId ||
                accessibleOrgs.length === 0 ||
                !newEvidenceReviewCycle ||
                (newEvidenceReviewCycle === "custom" &&
                  (!newEvidenceCustomDays ||
                    parseInt(newEvidenceCustomDays, 10) < 1 ||
                    parseInt(newEvidenceCustomDays, 10) > DAYS_MAX))
              }
            />
          ) : (
            <button
              onClick={handleCreateEvidenceUpload}
              disabled={uploadingCreateEvidenceFile || !createEvidenceFile}
              className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
            >
              {uploadingCreateEvidenceFile ? "Creating…" : "Upload & Finish"}
            </button>
          )
        }
      >
        {createEvidenceStep === "details" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <FormField label="Title" required>
                <FormInput
                  value={newEvidenceTitle}
                  onChange={setNewEvidenceTitle}
                  placeholder="e.g. SOC 2 Audit Report"
                  maxLength={EVIDENCE_TITLE_MAX}
                />
                {getTitleValidationError(newEvidenceTitle) ? (
                  <p className="text-xs text-red-500 mt-1">
                    {getTitleValidationError(newEvidenceTitle)}
                  </p>
                ) : (
                  newEvidenceTitle.length >= EVIDENCE_TITLE_MAX && (
                    <p className="text-xs text-amber-500 mt-1">
                      Maximum {EVIDENCE_TITLE_MAX} characters reached.
                    </p>
                  )
                )}
              </FormField>
            </div>
            <FormField label="Organization" required>
              {accessibleOrgsLoading ? (
                <div className="flex items-center gap-2 py-2">
                  <Loader2 className="size-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Loading</span>
                </div>
              ) : accessibleOrgs.length === 0 ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200/50 dark:border-red-500/20">
                  <Ban className="size-4 text-red-500 flex-shrink-0" />
                  <span className="text-sm text-red-600 dark:text-red-400">
                    No accessible organizations.
                  </span>
                </div>
              ) : accessibleOrgs.length === 1 ? (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#f5f7fb] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] text-sm">
                  <span className="font-medium">{accessibleOrgs[0].name}</span>
                </div>
              ) : (
                <FormSelect
                  options={accessibleOrgs.map((o) => ({
                    value: o.id,
                    // Name only: the short code is an internal identifier and
                    // the org id is what the backend is given.
                    label: o.name,
                  }))}
                  value={newEvidenceOrgId}
                  onChange={setNewEvidenceOrgId}
                  placeholder="Select organization"
                />
              )}
            </FormField>
            <FormField
              label="Tags"
              hint="Comma-separated tags for categorization"
              required
            >
              <FormInput
                value={newEvidenceTags}
                onChange={handleTagInputChange}
                placeholder="audit, soc2, 2024"
              />
              {tagSuggestions.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {tagSuggestions.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => {
                        const parts = newEvidenceTags.split(",");
                        parts[parts.length - 1] = tag;
                        setNewEvidenceTags(parts.join(", ") + ", ");
                        setTagSuggestions([]);
                      }}
                      className="px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-500/20"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              )}
            </FormField>
            <FormField label="Review Cycle" required>
              <FormSelect
                options={[
                  { value: "none", label: "No review required" },
                  { value: "90", label: "Quarterly" },
                  { value: "180", label: "Half-Yearly" },
                  { value: "365", label: "Annually" },
                  { value: "custom", label: "Custom" },
                ]}
                value={newEvidenceReviewCycle}
                onChange={(v) =>
                  setNewEvidenceReviewCycle(
                    v as "" | "none" | "90" | "180" | "365" | "custom",
                  )
                }
                placeholder="Select review cycle"
              />
            </FormField>
            {newEvidenceReviewCycle === "custom" ? (
              <FormField
                label="Days"
                required
                error={
                  newEvidenceCustomDays &&
                  (parseInt(newEvidenceCustomDays, 10) < 1 ||
                    parseInt(newEvidenceCustomDays, 10) > DAYS_MAX)
                    ? `Must be between 1 and ${DAYS_MAX} days`
                    : undefined
                }
                hint={!newEvidenceCustomDays ? "e.g. 30, 60, 120" : undefined}
              >
                <FormInput
                  type="number"
                  value={newEvidenceCustomDays}
                  onChange={setNewEvidenceCustomDays}
                  placeholder="Enter days"
                  min={1}
                  max={DAYS_MAX}
                />
              </FormField>
            ) : (
              <div />
            )}
            <div className="sm:col-span-2">
              <FormField label="Description">
                <FormTextarea
                  value={newEvidenceDescription}
                  onChange={setNewEvidenceDescription}
                  placeholder="Brief description"
                  rows={3}
                />
              </FormField>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 text-sm text-indigo-700 dark:text-indigo-400">
              <Upload className="size-4 flex-shrink-0" />
              <span>
                Upload a file for <strong>"{newEvidenceTitle}"</strong> to
                complete creation.
              </span>
            </div>
            <FormField label="Upload File" hint="Max size: 40 MB">
              <label
                className={`flex flex-col items-center justify-center gap-2 px-4 ${createEvidenceFile ? "py-3" : "py-10"} rounded-xl border-2 border-dashed transition-all ${createEvidenceFile ? "border-emerald-400 dark:border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-500/[0.04]" : "border-indigo-300 dark:border-indigo-500/30 hover:border-indigo-400 cursor-pointer"}`}
              >
                {createEvidenceFile ? (
                  <div className="flex items-center justify-between w-full px-3 py-2 rounded-lg bg-emerald-100/50 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/20 text-[12px]">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="size-3.5 text-emerald-600 flex-shrink-0" />
                      <span className="text-emerald-800 dark:text-emerald-300 font-medium truncate">
                        {createEvidenceFile.name}
                      </span>
                      <span className="text-[12px] text-emerald-600/60 flex-shrink-0">
                        {formatFileSize(createEvidenceFile.size)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCreateEvidenceFile(null);
                        if (createEvidenceFileInputRef.current)
                          createEvidenceFileInputRef.current.value = "";
                      }}
                      className="p-1 rounded text-emerald-400 hover:text-red-500 transition-colors flex-shrink-0"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="size-5 text-indigo-500 dark:text-indigo-400" />
                    <span className="text-[12px] font-medium text-indigo-600 dark:text-indigo-400">
                      Click to select a file
                    </span>
                    <span className="text-[10px] text-[#475569] dark:text-white/60">
                      PDF, images, and other formats supported
                    </span>
                  </>
                )}
                <input
                  ref={createEvidenceFileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) =>
                    setCreateEvidenceFile(e.target.files?.[0] ?? null)
                  }
                />
              </label>
            </FormField>
          </div>
        )}
      </Modal>

      {/* ── Edit Evidence Modal ── */}
      <Modal
        open={showEditEvidenceModal}
        onClose={() => {
          setShowEditEvidenceModal(false);
          setEditingEvidenceId(null);
        }}
        title="Edit Evidence"
        size="lg"
        closable={!savingEvidenceEdit}
        footer={
          <ModalFooterButtons
            onCancel={() => {
              setShowEditEvidenceModal(false);
              setEditingEvidenceId(null);
            }}
            onSubmit={handleSaveEvidenceFromModal}
            submitLabel={savingEvidenceEdit ? "Saving…" : "Save Changes"}
            submitDisabled={
              savingEvidenceEdit ||
              !editEvidenceTitle.trim() ||
              !!getTitleValidationError(editEvidenceTitle)
            }
          />
        }
      >
        <div className="grid grid-cols-1 gap-4">
          <FormField label="Title" required>
            <FormInput
              value={editEvidenceTitle}
              onChange={setEditEvidenceTitle}
              placeholder="e.g. SOC 2 Audit Report"
              maxLength={EVIDENCE_TITLE_MAX}
            />
            {getTitleValidationError(editEvidenceTitle) ? (
              <p className="text-xs text-red-500 mt-1">
                {getTitleValidationError(editEvidenceTitle)}
              </p>
            ) : (
              editEvidenceTitle.length >= EVIDENCE_TITLE_MAX && (
                <p className="text-xs text-amber-500 mt-1">
                  Maximum {EVIDENCE_TITLE_MAX} characters reached.
                </p>
              )
            )}
          </FormField>
          <FormField label="Description">
            <FormTextarea
              value={editEvidenceDescription}
              onChange={setEditEvidenceDescription}
              placeholder="Brief description"
              rows={3}
            />
          </FormField>
          <FormField
            label="Tags"
            hint="Comma-separated tags for categorization"
          >
            <FormInput
              value={editEvidenceTags}
              onChange={setEditEvidenceTags}
              placeholder="audit, soc2, 2024"
            />
          </FormField>
        </div>
      </Modal>

      {/* ── Preview Evidence Modal ── */}
      {previewEvidenceRow && previewEvidenceRow.hasFile && (
        <PreviewModal
          url={previewSupportingEvidenceFile(previewEvidenceRow.id)}
          fileName={previewEvidenceRow.fileName}
          downloadUrl={downloadSupportingEvidenceFile(previewEvidenceRow.id)}
          onClose={() => setPreviewEvidenceRow(null)}
        />
      )}

      {/* ── Lifecycle Action Modal ── */}
      <Modal
        open={showLifecycleModal}
        onClose={() => setShowLifecycleModal(false)}
        title={lifecycleActionLabel(lifecycleAction)}
        size="sm"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowLifecycleModal(false)}
            onSubmit={handleLifecycleAction}
            submitLabel={
              lifecycleProcessing
                ? "Processing…"
                : lifecycleActionLabel(lifecycleAction)
            }
            submitDisabled={
              lifecycleProcessing ||
              ((lifecycleAction === "reject" || lifecycleAction === "retire") &&
                !lifecycleReason.trim())
            }
          />
        }
      >
        <p className="text-sm text-foreground">
          {lifecycleAction === "submit" &&
            "Submit this document for review. It will move from Draft to In Review."}
          {lifecycleAction === "approve" &&
            "Approve this document. It will move to Approved status, ready to be published."}
          {lifecycleAction === "publish" &&
            "Publish this document. It will go live and the review cycle will begin."}
          {lifecycleAction === "reject" &&
            "Reject this document. It will return to Draft status with a rejection reason."}
          {lifecycleAction === "new-version" &&
            "Create a new draft version of this published document."}
          {lifecycleAction === "retire" &&
            "Withdraw this document. It will no longer be active and cannot be restored."}
        </p>
        {lifecycleAction === "reject" && (
          <FormField label="Reason" required>
            <FormTextarea
              placeholder="Reason for rejection…"
              value={lifecycleReason}
              onChange={setLifecycleReason}
              rows={3}
            />
          </FormField>
        )}
        {lifecycleAction === "retire" && (
          <FormField label="Reason" required>
            <FormTextarea
              placeholder="Reason for withdraw…"
              value={lifecycleReason}
              onChange={setLifecycleReason}
              rows={2}
            />
          </FormField>
        )}
      </Modal>

      {/* ── Delete Confirmation Modal ── */}
      <DeleteConfirmDialog
        open={deleteModalOpen}
        type={deleteTargetType === "governance" ? "Document" : "Evidence"}
        name={
          deleteTargetType === "governance"
            ? documents.find((d) => d.id === deleteTargetId)?.title || ""
            : evidence.find((e) => e.id === deleteTargetId)?.title || ""
        }
        deleting={!!deleting}
        showReason={false}
        onCancel={() => {
          setDeleteModalOpen(false);
          setDeleteTargetId(null);
        }}
        onConfirm={() => confirmDelete()}
      />

      {/* ── File Upload Modal ── */}
      {uploadTargetType === "governance" ? (
        <GovernanceUploadModal
          open={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          documentId={selectedGovDoc?.id ?? ""}
          documentTitle={selectedGovDoc?.title ?? "Governance document"}
          orgName={selectedGovDoc?.owningOrgUnitName ?? undefined}
          docType={selectedGovDoc?.documentType ?? undefined}
        />
      ) : (
        <Modal
          open={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          title="Upload File"
          size="sm"
          footer={
            <ModalFooterButtons
              onCancel={() => setShowUploadModal(false)}
              onSubmit={handleUploadFile}
              submitLabel={uploading ? "Uploading…" : "Upload"}
              submitDisabled={uploading || !uploadFile}
            />
          }
        >
          <div className="space-y-3">
            <label
              className={`flex flex-col items-center justify-center gap-2 px-4 ${uploadFile ? "py-3" : "py-10"} rounded-xl border-2 border-dashed transition-all ${uploadFile ? "border-emerald-400 dark:border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-500/[0.04]" : "border-indigo-300 dark:border-indigo-500/30 hover:border-indigo-400 cursor-pointer"}`}
            >
              {uploadFile ? (
                <div className="flex items-center justify-between w-full px-3 py-2 rounded-lg bg-emerald-100/50 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/20 text-[12px]">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="size-3.5 text-emerald-600 flex-shrink-0" />
                    <span className="text-emerald-800 dark:text-emerald-300 font-medium truncate">
                      {uploadFile.name}
                    </span>
                    <span className="text-[12px] text-emerald-600/60 flex-shrink-0">
                      {formatFileSize(uploadFile.size)}
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="size-5 text-indigo-500 dark:text-indigo-400" />
                  <span className="text-[12px] font-medium text-indigo-600 dark:text-indigo-400">
                    Click to select a file
                  </span>
                </>
              )}
              <input
                type="file"
                className="hidden"
                onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
              />
            </label>
          </div>
        </Modal>
      )}
    </>
  );
}
